<script setup>
  import { ref, computed, useSlots } from 'vue'
  import { useSheetController } from '@/composables/useSheetsController';
  import { useFocusElement } from "@/composables/useFocusableElements";
  import { useMapAdStore } from '@/stores/MapAdStore';
  import CloseButton from '@/components/CloseButton.vue';
  import Icon from '@/components/icons/Icon.vue';

  const props = defineProps({
    type: {
      type: String,
      required: true
    },
    transtionName: {
      type: String,
      required: true
    },
    useIcon: {
      type: Boolean,
      default: false
    },
    contentOnAfterEnter: {
      type: Function,
      required: false
    },
    autoFocus: {
      type: Boolean,
      default: true
    }
  });

  const adStore = useMapAdStore();
  const slots = useSlots();

  const sheetContainer = ref(null);
  const activeElement = ref(null);
  const hasEntered = ref(false);

  const hasIcon = computed(() => props.useIcon ? true : null);
  const hasHistory = computed(() => useSheetController.hasHistory(props.type) ? true : null);
  const transtionName = computed(() => hasHistory.value ? 'content-history-foward-slide' : props.transtionName);

  const { focusFirstElement } = useFocusElement(sheetContainer);

  const showFooter = computed(() => !!slots.footer);

  function handleSheetClose() {
    useSheetController.toggleSheet(props.type);
    activeElement.value.focus();
  }

  function handleEscapeKeyUp() {
    if (document.documentElement.hasAttribute('data-popup-open')) return;

    handleSheetClose();
  }

  function onAfterEnter() {
    activeElement.value = document.activeElement;
    hasEntered.value = true;

    if (props.autoFocus) setTimeout(focusFirstElement, 100);

    adStore.refreshAd();
    
    if (props.contentOnAfterEnter && typeof props.contentOnAfterEnter === 'function') props.contentOnAfterEnter();
  }

  function handleSheetGoBack() {
    useSheetController.goBackInHistory(props.type);
  }
</script>

<template>
  <Transition
    @after-enter="onAfterEnter"
    :name="transtionName"
    mode="out-in"
    appear>  
    <div
      @keydown.esc="handleEscapeKeyUp"
      class="jwx-sheet__container"
      ref="sheetContainer"
    >
      <header
        class="jwx-sheet__header"
        :data-has-icon="hasIcon"
        :data-has-history="hasHistory"
      >
        <button
          v-if="hasHistory"
          @click.prevent="handleSheetGoBack"
          class="jwx-sheet__go-back-button"
          aria-label="Go Back"
        >
          <Icon 
            name="chevron-left"
            class="jwx-sheet__go-back-icon"
          />
        </button>
        <slot name="header"></slot>
        <CloseButton
          @click.prevent="handleSheetClose" 
          class="jwx-sheet__close-button"
        />
      </header>
      <main class="jwx-sheet__body">
        <slot name="body"></slot>
      </main>
      <footer
        v-if="showFooter" 
        class="jwx-sheet__footer jwx-sheet__footer--sticky"
      >
        <slot name="footer"></slot>
      </footer>
    </div>
  </Transition>
</template>